/**
 * Unauthorized Access Page for Rider Gateway
 * Shown when a non-rider user lands on the rider portal.
 */

import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAppDispatch } from '../hooks/reduxHooks';
import { logout } from '../store/slices/authSlice';
import MobileAuthLayout from '../components/templates/MobileAuthLayout';
import { r } from '../theme/riderTheme';
import {
  ButtonChevron,
  PrimaryButton,
} from '../theme/surfacePrimitives';

const STAFF_PORTAL_URL = 'https://staff.app.gogoronp-nebula.com/';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.section`
  width: 100%;
  max-width: 380px;
  padding: 28px 24px 24px;
  background: ${({ theme }) => r(theme).surface.sheet};
  color: ${({ theme }) => r(theme).text.primary};
  border-radius: 20px;
  box-shadow:
    0 1px 0 ${({ theme }) => r(theme).surface.edge},
    0 20px 60px -20px rgba(0, 0, 0, 0.45);
  text-align: center;
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const Eyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).accent.alert};
  margin-bottom: 14px;
`;

const IndicatorRing = styled.div`
  margin: 0 auto 18px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 25, 35, 0.06);
  border: 1px solid rgba(255, 25, 35, 0.32);
`;

const Cross = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => r(theme).accent.alert};
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Body = styled.p`
  margin: 0 0 8px;
  font-family: 'Geist', sans-serif;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Sub = styled.p`
  margin: 0 0 22px;
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.muted};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StaffLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 46px;
  padding: 0 18px;
  background: transparent;
  border: 1px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 12px;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-decoration: none;
  transition: background 180ms ease, border-color 180ms ease;

  &:hover {
    background: rgba(18, 18, 21, 0.04);
    border-color: ${({ theme }) => r(theme).text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const Footnote = styled.div`
  margin-top: 22px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const UnauthorizedPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <MobileAuthLayout>
      <Card>
        <Eyebrow>Access · Restricted</Eyebrow>
        <IndicatorRing aria-hidden>
          <Cross>×</Cross>
        </IndicatorRing>
        <Title>Unauthorized access</Title>
        <Body>You don't have permission to access the Rider Portal.</Body>
        <Sub>This area is restricted to riders only.</Sub>

        <Actions>
          <PrimaryButton onClick={handleLogout} aria-label="Sign out and try again">
            <span>Sign out &amp; try again</span>
            <ButtonChevron aria-hidden>→</ButtonChevron>
          </PrimaryButton>
          <StaffLink href={STAFF_PORTAL_URL}>
            <span>Go to Staff Dashboard</span>
          </StaffLink>
        </Actions>

        <Footnote>If this looks wrong, contact support.</Footnote>
      </Card>
    </MobileAuthLayout>
  );
};

export default UnauthorizedPage;
